/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';
import { ArrowDownIcon, ArrowUpIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/admin-shared';

import { useSortablePaginatedTableContext } from './SortablePaginatedTableProvider';
import type { SortDirection } from './SortablePaginatedTableProvider';
import { Th } from './styledTableComponents';

const activeThBottomBorder = css`
  border-bottom: 2px solid var(--cds-color-blue-800);
`;

const basicTh = css`
  cursor: pointer;

  &:hover {
    background-color: var(--cds-color-blue-25);
    ${activeThBottomBorder}
  }

  &:hover,
  button:focus {
    svg {
      visibility: visible;
    }
  }
`;

const styles = {
  hiddenButOccupiesSpace: css`
    visibility: hidden;
  `,
  thInner: css`
    display: flex;
    align-items: center;
  `,
  thLabel: css`
    user-select: none; /* Disallow accidental text highlighting on click */
    margin-left: var(--cds-spacing-100);
  `,
  unstyledButton: css`
    all: initial;
    cursor: pointer;
  `,
  isSorting: css`
    ${basicTh}
    ${activeThBottomBorder}
  `,
};

const DEFAULT_INITIAL_SORT_DIRECTION: SortDirection = 'ASC';
const InitialSortIcon = DEFAULT_INITIAL_SORT_DIRECTION === 'ASC' ? ArrowUpIcon : ArrowDownIcon;

interface SortableThProps {
  sortKey: string;
  /** The label for display and for screen readers */
  label: string;
  /** The label to be read out by screen readers when describing the affects of a sort on this column. Eg: "Email" will be read out as "Click this button to sort by Email ascending" */
  sortI18nNoun: string;
}

const SortableThButton: React.LegacyFunctionComponentWithChildren<SortableThProps> = ({
  sortKey,
  sortI18nNoun,
  children,
}) => {
  const { calculateEffectOfHandleSortClick, handleSortClick } = useSortablePaginatedTableContext();
  const predictedClickResult = calculateEffectOfHandleSortClick(sortKey);

  const ascString = _t('Sort the table by #{noun} in ascending order', { noun: sortI18nNoun });
  const descString = _t('Sort the table by #{noun} in descending order', { noun: sortI18nNoun });

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleSortClick(sortKey, sortI18nNoun);
      }}
      type="button"
      aria-label={predictedClickResult.direction === 'ASC' ? ascString : descString}
      css={styles.unstyledButton}
    >
      {children}
    </button>
  );
};

const SortableThIcon = ({ sortKey }: SortableThProps) => {
  const { sort } = useSortablePaginatedTableContext();
  if (sort.key !== sortKey) {
    return <InitialSortIcon size="small" css={styles.hiddenButOccupiesSpace} />;
  }

  if (sort.direction === 'DESC') {
    return <ArrowDownIcon size="small" />;
  }

  return <ArrowUpIcon size="small" />;
};

export const SortableTh = (props: SortableThProps) => {
  const { sortKey, sortI18nNoun, label } = props;
  const { handleSortClick, sort } = useSortablePaginatedTableContext();
  const isSorting = sort.key === sortKey;

  return (
    <Th
      scope="col"
      css={isSorting ? styles.isSorting : basicTh}
      onClick={() => handleSortClick(sortKey, sortI18nNoun)}
      aria-label={label}
    >
      <div css={styles.thInner}>
        <SortableThButton {...props}>
          <SortableThIcon {...props} />
        </SortableThButton>
        <Typography2 variant="subtitleMedium" color="supportText" css={styles.thLabel} display="inline" component="div">
          {label}
        </Typography2>
      </div>
    </Th>
  );
};
